import type { ReactNode } from 'react';
import type { SeasonPreference } from '@features/seasonal-theme/types';

import { useState } from 'react';
import { Check, Palette, CalendarDays } from 'lucide-react';

import { cn } from '@shared/utils/cn';
import { Button } from '@shared/components/ui/button';
import { useIsDesktop } from '@shared/hooks/use-media-query';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shared/components/ui/tooltip';
import { Sheet, SheetTitle, SheetHeader, SheetContent, SheetDescription } from '@shared/components/ui/sheet';

import { SeasonIcon } from '@features/seasonal-theme/components/season-icon';
import { useActiveSeason } from '@features/seasonal-theme/hooks/use-active-season';
import { useSeasonPreference } from '@features/seasonal-theme/hooks/use-season-preference';
import { formatSeasonRange, seasonsByCalendar } from '@features/seasonal-theme/utils/format-season-range';

const TITLE = 'Thème saisonnier';
// Thème de base (équivalent de `themeConfig.palette` de Minimal, encore utilisé par les écrans MUI du workshop)
const DEFAULT_COLORS = ['#2563EB', '#334155'];

const Swatches = ({ colors }: { colors: string[] }) => (
  <span aria-hidden className="flex gap-1">
    {colors.map((color) => (
      <span
        className="size-3.5 rounded-full shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)]"
        key={color}
        style={{ backgroundColor: color }}
      />
    ))}
  </span>
);

const GenericIcon = ({ children }: { children: ReactNode }) => (
  <span aria-hidden className="flex w-6 justify-center text-lg text-muted-foreground [&>svg]:size-[18px]">
    {children}
  </span>
);

type OptionProps = {
  selected: boolean;
  onSelect: () => void;
  icon: ReactNode;
  title: string;
  caption: string;
  colors: string[];
};

const Option = ({ selected, onSelect, icon, title, caption, colors }: OptionProps) => (
  <button
    aria-checked={selected}
    className={cn(
      'flex min-h-14 w-full items-center gap-3 rounded-lg border-0 px-3 py-2 text-left transition-colors outline-none hover:bg-accent focus-visible:bg-accent',
      selected ? 'bg-accent' : 'bg-transparent'
    )}
    onClick={onSelect}
    role="menuitemradio"
    type="button"
  >
    {icon}
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="truncate text-sm font-semibold">{title}</span>
      <span className="truncate text-xs text-muted-foreground">{caption}</span>
    </span>
    <Swatches colors={colors} />
    <span aria-hidden className={cn('flex w-4 text-primary', selected ? 'visible' : 'invisible')}>
      <Check className="size-4" />
    </span>
  </button>
);

const SeasonOptions = () => {
  const { preference, setPreference } = useSeasonPreference();
  const { datedSeason } = useActiveSeason();
  const year = new Date().getFullYear();

  const select = (value: SeasonPreference) => () => setPreference(value);

  return (
    <div aria-label={TITLE} className="flex flex-col gap-1 p-2" role="menu">
      <Option
        caption={datedSeason ? `Selon la date · ${datedSeason.label}` : 'Selon la date · aucun événement'}
        colors={datedSeason ? [datedSeason.palette.primary.main, datedSeason.palette.secondary.main] : DEFAULT_COLORS}
        icon={
          <GenericIcon>
            <CalendarDays />
          </GenericIcon>
        }
        onSelect={select('auto')}
        selected={preference === 'auto'}
        title="Automatique"
      />
      <Option
        caption="Couleurs habituelles"
        colors={DEFAULT_COLORS}
        icon={
          <GenericIcon>
            <Palette />
          </GenericIcon>
        }
        onSelect={select('off')}
        selected={preference === 'off'}
        title="Désactivé"
      />
      <div className="my-1 h-px bg-border" />
      {seasonsByCalendar(year).map((season) => (
        <Option
          caption={`${formatSeasonRange(season, year)}${datedSeason?.id === season.id ? ' · en cours' : ''}`}
          colors={[season.palette.primary.main, season.palette.secondary.main]}
          icon={<SeasonIcon season={season} />}
          key={season.id}
          onSelect={select(season.id)}
          selected={preference === season.id}
          title={season.label}
        />
      ))}
    </div>
  );
};

/**
 * Header button opening the seasonal theme choice: a popover on desktop, a bottom sheet on mobile.
 * The menu stays open after a choice so several seasons can be previewed in a row.
 */
export const SeasonMenu = () => {
  const isDesktop = useIsDesktop();
  const { season } = useActiveSeason();
  const [open, setOpen] = useState(false);

  const trigger = (
    <Button aria-label={TITLE} className="size-10" size="icon" variant="ghost">
      {season ? <SeasonIcon season={season} size={22} /> : <Palette className="size-[18px]" />}
    </Button>
  );

  if (isDesktop) {
    return (
      <Popover onOpenChange={setOpen} open={open}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{TITLE}</TooltipContent>
        </Tooltip>
        <PopoverContent align="end" className="max-h-[min(560px,calc(100dvh-96px))] w-85 overflow-y-auto p-0">
          <p className="px-5 pt-4 pb-1 text-lg font-semibold">{TITLE}</p>
          <SeasonOptions />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <Button aria-label={TITLE} className="size-10" onClick={() => setOpen(true)} size="icon" variant="ghost">
        {season ? <SeasonIcon season={season} size={22} /> : <Palette className="size-[18px]" />}
      </Button>
      <SheetContent className="max-h-[85dvh] pb-[env(safe-area-inset-bottom)]" side="bottom">
        <SheetHeader className="pb-0">
          <SheetTitle>{TITLE}</SheetTitle>
          <SheetDescription className="sr-only">Choisir le thème saisonnier</SheetDescription>
        </SheetHeader>
        <div className="overflow-y-auto">
          <SeasonOptions />
        </div>
      </SheetContent>
    </Sheet>
  );
};
